<template>
  <div v-loading="isLoading">
    <el-table
      ref="schedulesTable"
      :data="schedules"
      stripe
      size="small"
    >
      <el-table-column type="expand">
        <template slot-scope="props">
          <SchedulePickupsTablePeak :schedule="props.row" />
        </template>
      </el-table-column>

      <el-table-column
        label="Account"
        prop="template"
        min-width="320px"
        show-overflow-tooltip
      >
        <template slot-scope="scope">
          <router-link
            v-if="scope.row.template && scope.row.template.account"
            :to="{ name:'manage-accounts-account', params: { accountId: scope.row.template.account.id } }"
          >
            {{ scope.row.template.account.name || '-' }}
          </router-link>
        </template>
      </el-table-column>
      <el-table-column
        label="Carrier"
        width="180px"
        show-overflow-tooltip
        prop="template"
        :formatter="(row, col, cellValue) => cellValue ? (cellValue.carrier ? cellValue.carrier.name || '-' : '-') : '-'"
      />
      <el-table-column
        label="Sender"
        width="240px"
        show-overflow-tooltip
        prop="sender"
      >
        <template slot-scope="scope">
          <u
            v-if="scope.row.template && scope.row.template.sender"
            v-tooltip="scope.row.template.sender ? formatAddressName(scope.row.template.sender) : '-'"
          >
            {{ scope.row.template.sender ? scope.row.template.sender.organisation_name : '-' }}
          </u>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column
        label="Active From"
        width="120px"
        prop="date_from"
        :formatter="(row, col, cellValue) => cellValue ? (($moment(cellValue).format('DD/MM/YYYY')) || '-') : '-'"
      />
      <el-table-column
        label="Active To"
        width="120px"
        prop="date_to"
        :formatter="(row, col, cellValue) => cellValue ? (($moment(cellValue).format('DD/MM/YYYY')) || 'Indefinite') : 'Indefinite'"
      />
      <el-table-column
        label="Last Created Pickup"
        width="180px"
        prop="created_pickups"
      >
        <template slot-scope="scope">
          <router-link
            v-if="getLatestPickup(scope.row)"
            :to="{ name: 'customerservice-pickups-pickup', params: { pickupId: getLatestPickup(scope.row).id } }"
          >
            {{ getLatestPickup(scope.row).booking_reference }}
          </router-link>
          <div v-else>
            -
          </div>
        </template>
      </el-table-column>
      <el-table-column
        label="Next Scheduled"
        width="240px"
      >
        <template slot-scope="scope">
          <el-tag
            v-if="scope.row.is_paused"
            type="warning"
            size="mini"
          >
            Paused
          </el-tag>

          <template v-else>
            {{ $moment(getNextScheduledDay(scope.row)).format('ddd, MMM D, YYYY') }}
          </template>
        </template>
      </el-table-column>
      <el-table-column
        v-if="canUpdate || canDelete"
        label="Actions"
        align="right"
        width="75px"
      >
        <template slot-scope="scope">
          <el-dropdown style="margin-left: 10px;">
            <el-button size="mini">
              <FontAwesomeIcon :icon="['far', 'ellipsis-h']" />
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-if="canUpdate"
                @click.native="modifySchedule(scope.row)"
              >
                Modify Schedule
              </el-dropdown-item>
              <el-dropdown-item
                v-if="canUpdate"
                @click.native="modifyTemplate(scope.row)"
              >
                Modify Template
              </el-dropdown-item>
              <hr
                v-if="canUpdate"
                class="action-seperator"
              >
              <el-dropdown-item
                v-if="canUpdate"
                @click.native="skipToday(scope.row)"
              >
                Skip Today
              </el-dropdown-item>
              <el-dropdown-item
                v-if="canUpdate"
                @click.native="skipTomorrow(scope.row)"
              >
                Skip Tomorrow
              </el-dropdown-item>
              <hr
                v-if="canUpdate"
                class="action-seperator"
              >
              <el-dropdown-item
                v-if="canUpdate && !scope.row.is_paused"
                @click.native="pauseIndefinitely(scope.row)"
              >
                Pause Indefinitely
              </el-dropdown-item>
              <el-dropdown-item
                v-if="canUpdate && scope.row.is_paused"
                @click.native="unpauseIndefinitely(scope.row)"
              >
                Unpause Indefinitely
              </el-dropdown-item>
              <el-dropdown-item
                v-if="canDelete"
                style="color: #f56c6c;"
                @click.native="deleteSchedule(scope.row)"
              >
                Delete Schedule
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
      </el-table-column>
    </el-table>
    <!-- Pagination -->
    <br>
    <div style="text-align: center;">
      <el-pagination
        :current-page.sync="schedules.page"
        :page-size.sync="schedules.limit"
        layout="total, sizes, prev, pager, next, jumper"
        :total="schedules.total"
        @size-change="reload"
        @current-change="reload"
      />
    </div>
    <!-- Edit Form Dialog -->
    <SchedulePickupDialog
      v-if="isShowEditFormDialog"
      ref="editFormDialog"
      :schedule="schedule"
      @close="() => {
        isShowEditFormDialog = false
        schedule = null
      }"
      @updatedSchedule="reload()"
      @reload="reload()"
    />
  </div>
</template>

<script>
import SchedulePickupsTablePeak from '@/components/Application/Manage/SchedulePickups/SchedulePickupsTablePeak'
import SchedulePickupDialog from '@/components/Application/Manage/SchedulePickup/SchedulePickupDialog'
import { UPDATE, DELETE } from '@/plugins/user'

export default {
  name: 'SchedulePickupsTable',
  components: {
    SchedulePickupsTablePeak,
    SchedulePickupDialog
  },
  props: {
    title: {
      type: String,
      required: false,
      default () {
        return ''
      }
    },
    description: {
      type: String,
      required: false,
      default () {
        return ''
      }
    },
    filters: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      schedules: [],
      page: 1,
      perPage: 10,
      total: 0,
      isLoading: true,
      isShowEditFormDialog: false,
      schedule: null
    }
  },
  computed: {
    canUpdate () {
      return this.$user.can(UPDATE, this.$sdk.scheduledpickup)
    },
    canDelete () {
      return this.$user.can(DELETE, this.$sdk.scheduledpickup)
    }
  },
  methods: {
    loadTable () {
      this.isLoading = true
      this.$sdk.scheduledpickup.list(builder => {
        builder.include = ['skips', 'created_pickups', 'template.account', 'template.carrier', 'template.sender', 'template.receiver']
        builder.page = 1
        builder.limit = 10
        builder.order = 'created_at:desc'
        builder.filters = this.filters
      }).then(res => { this.schedules = res })
        .finally(() => { this.isLoading = false })
    },
    async reload () {
      this.isLoading = true
      try {
        await this.schedules.reload(builder => { builder.filters = this.filters })
      } catch {
        this.$message.error('Failed to load scheduled pickups')
      }
      this.isLoading = false
    },
    formatAddressName (address) {
      if (address) {
        const fields = ['organisation_name', 'first_name', 'last_name', 'address_line1', 'address_line2', 'locality', 'administrative_area', 'country']
        let stringList = []
        fields.map(field => {
          stringList.push(address[field] || '-')
        })
        stringList = stringList.filter(s => s !== '-')
        return stringList.join(', ')
      }
      return '-'
    },
    getLatestPickup (schedule) {
      if (schedule.created_pickups) {
        const createdList = this.$_.sortBy(schedule.created_pickups, value => value.created_at)
        return this.$_.head(createdList)
      }
    },
    getNextScheduledDay (attributes) { // Copied from old OF
      const now = new Date()
      const schedule = [
        !!attributes.sunday_ready_time,
        !!attributes.monday_ready_time,
        !!attributes.tuesday_ready_time,
        !!attributes.wednesday_ready_time,
        !!attributes.thursday_ready_time,
        !!attributes.friday_ready_time,
        !!attributes.saturday_ready_time
      ]

      return new Date(now.getTime()).setDate(now.getDate() + (7 + this.$_.indexOf(schedule, true) - now.getDay() - 1) % 7 + 1)
    },
    modifySchedule (schedule) {
      this.schedule = schedule.clone()
      this.isShowEditFormDialog = true
    },
    modifyTemplate (schedule) {
      this.schedule = schedule.clone()
      this.isShowEditFormDialog = true
      this.$nextTick(() => {
        this.$refs.editFormDialog.activeName = 'third'
      })
    },
    skipToday (schedule) {
      this.$confirm('Are you sure you want to skip todays Scheduled Pickup? Note that if a pickup has already been created for today, this will not delete the created pickup.', 'Skip Pickup Schedule', {
        confirmButtonText: 'Skip Today',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        let bodyData = {
          skips: [{
            date_from: this.$moment().format('YYYY-MM-DD'),
            date_to: this.$moment().format('YYYY-MM-DD')
          }]
        }
        this.$sdk.scheduledpickup.update(schedule.id, bodyData, builder => {
          builder.include = ['skips', 'created_pickups', 'template.account', 'template.carrier', 'template.sender', 'template.receiver']
        }).then(() => {
          this.reload()
          this.$message({
            type: 'success',
            message: 'Successfully skipped todays Pickup Schedule'
          })
        }).catch(err => {
          this.$message({
            type: 'danger',
            message: 'Failed to skip todays Pickup Schedule'
          })
          throw err
        })
      }).catch(() => {})
    },
    skipTomorrow (schedule) {
      this.$confirm('Are you sure you want to skip tomorrows Scheduled Pickup?', 'Skip Pickup Schedule', {
        confirmButtonText: 'Skip Tomorrow',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        let bodyData = {
          skips: [{
            date_from: this.$moment().add(1, 'day').format('YYYY-MM-DD'),
            date_to: this.$moment().add(1, 'day').format('YYYY-MM-DD')
          }]
        }
        this.$sdk.scheduledpickup.update(schedule.id, bodyData, builder => {
          builder.include = ['skips', 'created_pickups', 'template.account', 'template.carrier', 'template.sender', 'template.receiver']
        }).then(() => {
          this.reload()
          this.$message({
            type: 'success',
            message: 'Successfully skipped todays Pickup Schedule'
          })
        }).catch(err => {
          this.$message({
            type: 'danger',
            message: 'Failed to skip todays Pickup Schedule'
          })
          throw err
        })
      }).catch(() => {})
    },
    unpauseIndefinitely (schedule) {
      this.$confirm('Are you sure you want to unpause this Scheduled Pickup?', 'Unpause Pickup Schedule', {
        confirmButtonText: 'Unpause',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        let bodyData = {
          is_paused: false
        }
        this.$sdk.scheduledpickup.update(schedule.id, bodyData, builder => {
          builder.include = ['skips', 'created_pickups', 'template.account', 'template.carrier', 'template.sender', 'template.receiver']
        }).then(() => {
          this.reload()
          this.$message({
            type: 'success',
            message: 'Successfully unpaused Pickup Schedule'
          })
        }).catch(err => {
          this.$message({
            type: 'danger',
            message: 'Failed to unpause Pickup Schedule'
          })
          throw err
        })
      }).catch(() => {})
    },
    pauseIndefinitely (schedule) {
      this.$confirm('Are you sure you want to pause this Scheduled Pickup?', 'Pause Pickup Schedule', {
        confirmButtonText: 'Pause Indefinitely',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        let bodyData = {
          is_paused: true
        }
        this.$sdk.scheduledpickup.update(schedule.id, bodyData, builder => {
          builder.include = ['skips', 'created_pickups', 'template.account', 'template.carrier', 'template.sender', 'template.receiver']
        }).then(() => {
          this.reload()
          this.$message({
            type: 'success',
            message: 'Successfully paused Pickup Schedule'
          })
        }).catch(err => {
          this.$message({
            type: 'danger',
            message: 'Failed to pause Pickup Schedule'
          })
          throw err
        })
      }).catch(() => {})
    },
    deleteSchedule (schedule) {
      this.$confirm('Are you sure you want to delete this Pickup Schedule? This action cannot be undone.', 'Deleting Pickup Schedule', {
        confirmButtonText: 'Deleting Schedule',
        cancelButtonText: 'Cancel',
        type: 'error'
      }).then(() => {
        this.$sdk.scheduledpickup.delete(schedule.id)
          .then(() => {
            this.reload()
            this.$message({
              type: 'success',
              message: 'Schedule Pickup has been deleted'
            })
          }).catch(err => {
            this.$message({
              type: 'danger',
              message: 'Failed to delete schedule pickup'
            })
            throw err
          })
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.action-seperator {
  margin: 5px 0px 5px 0px;
}
</style>
